/**
 * ADAPTIVE INTERPOLATION DELAY
 *
 * Remote cats are rendered in the past, so there is always a snapshot either
 * side of the render clock to blend between.  Too little delay and the clock
 * runs off the front of the buffer and every dropped packet is a visible
 * stutter; too much and the neighbourhood is a street of cats walking where
 * other people's cats were a third of a second ago.
 *
 * A fixed delay has to be sized for the worst link this game will ever meet,
 * which taxes every good one.  This sizes it per client, from what that client
 * actually measures: one snapshot interval (so a bracket exists at all) plus a
 * jitter margin (so late packets do not empty the buffer).  The shape is a
 * port of BreachProtocol's InterpDelay, because that class carries two hard
 * lessons that cost real measurement to learn; both are kept, restated here
 * against OUR numbers -- 20 Hz snapshots (50 ms apart), not that game's 30.
 *
 * --- WHY THIS DOES NOT MEASURE GAPS BETWEEN MESSAGE HANDLERS ---------------
 *
 * The obvious estimator -- the spread of the gaps between noteArrival calls --
 * is wrong in the most expensive direction.  The renderer, the socket and this
 * clock share ONE thread, so the handler runs when the thread gets round to
 * the message, not when the message arrived: a client drawing 24 ms frames
 * services the socket only at frame boundaries, and a perfectly even stream
 * reaches this class re-gridded onto those boundaries.  The class then reads
 * its own GPU being busy as link jitter and widens -- rendering every remote
 * cat further into the past on exactly the machine that can least afford it.
 * BreachProtocol measured the naive version inventing 41 ms of extra delay out
 * of nothing but frame load.  `MessageEvent.timeStamp` is no rescue: Chromium
 * stamps at DISPATCH, not arrival (measured there: nine snapshots queued
 * behind a 300 ms stall all carry the stamp of the moment they were finally
 * dispatched), so the browser hands us no true arrival time at all.
 *
 * So the estimate is built from two clocks a stall cannot corrupt:
 *
 *   1. THE SERVER'S OWN CLOCK.  `snap.time` is stamped by the server tick, so
 *      it is evenly spaced whatever this machine is doing.  Each arrival
 *      measured as `handled - sent` gives a one-way latency whose VARIATION is
 *      what a jitter buffer exists to absorb, and taking the base interval
 *      from the server's own spacing means a dropped snapshot can no longer
 *      masquerade as a slower snapshot rate.  One wrinkle of ours: protocol.js
 *      rounds the stamp to 10 ms (round2 on seconds).  Harmless, but only
 *      because 10 divides the 50 ms tick: on a steady tick the rounding is a
 *      CONSTANT offset, and this class only ever reads spreads -- the harness
 *      measures 0.0 ms of added delay.  It starts to bite exactly when the
 *      tick itself wobbles across a 10 ms boundary, and that wobble is
 *      genuine lateness the buffer should be covering anyway.  Worth knowing
 *      if TICK_RATE ever stops dividing evenly into round2's grid.
 *
 *   2. THIS THREAD'S OWN RESPONSIVENESS.  update() runs at the top of the
 *      frame, so the time from a frame starting to a message being handled is
 *      an honest measure of how long WE made that message wait.  Lateness we
 *      inflicted on ourselves is not lateness a bigger buffer can fix -- when
 *      this thread stalls, the render clock stalls with it, so the buffer was
 *      not draining while we were not looking.
 *
 * What 20 Hz changes, concretely: the window below spans 1.2 s instead of
 * 0.8 (nothing here is a firefight -- reacting to a changed link inside a
 * second or so is plenty, and the longer window steadies the p90 against the
 * stamp quantisation above); the "unrecoverable stall" threshold is the 50 ms
 * step, so a 60 Hz renderer's worst frame almost never trips it; and the
 * frame-grid correction matters MORE, not less -- a 16.7 ms display grid
 * against 50 ms snapshots can park nearly its whole width inside one window's
 * spread, which times INTERP_JITTER_MULT would be ~42 ms of invented delay.
 *
 * Every judgement fails toward widening: corrections are applied only on
 * positive evidence this thread was blocked, taken as the MINIMUM over the
 * window, so one responsive moment cancels the whole correction.  Widening on
 * genuine link jitter is load-bearing and survives all of this.
 *
 * Dependency-free above `shared/constants.js` on purpose: the throwaway
 * harness drives this class in node with synthetic clocks, and it must behave
 * there exactly as it does inside requestAnimationFrame.
 */
import { INTERP_JITTER_MULT, INTERP_MAX, INTERP_MIN } from "@catworld/shared";

/** Arrivals kept for the estimate: 1.2 s at 20 Hz.  See the header for why
    this is longer, in samples AND seconds, than BreachProtocol's. */
const WINDOW = 24;
/** Below this there is not enough evidence to move off the pessimistic
    start value. */
const MIN_SAMPLES = 6;
/** Neither a local gap nor a server gap this large is jitter -- a
    backgrounded tab, a breakpoint, a server restart -- and treating one as
    jitter would pin the delay at the ceiling on a single outlier. */
const ABSURD_MS = 1000;

export class InterpDelay {
  constructor(initial = INTERP_MAX) {
    // Fixed-capacity rings, written in place: this runs inside the socket
    // callback at 20 Hz and the frame callback at display rate, so it obeys
    // their rule -- no allocation after construction.
    /** `handled - sent`, ms, against the server's clock.  Arbitrary constant
        offset; only the spread is ever read. */
    this._late = new Float64Array(WINDOW);
    /** The server's own spacing between consecutive snapshots, ms. */
    this._step = new Float64Array(WINDOW);
    /** Per-sample ceiling on the lateness THIS THREAD manufactured. */
    this._self = new Float64Array(WINDOW);
    /** Sort scratch: sorting a live ring would reorder samples still being
        accumulated into. */
    this._scratch = new Float64Array(WINDOW);
    this._n = 0;
    this._w = 0;

    this._lastArrival = 0;
    this._lastServer = 0;
    this._hasPrev = false;
    /** Wall clock at the last frame callback. */
    this._lastFrameAt = 0;
    /** Consecutive arrivals discarded as "this thread was blocked" -- see the
        escape hatch in noteArrival. */
    this._blockedRun = 0;

    /** Where `value` is heading.  Recomputed on arrival (20 Hz), not per
        frame: percentiles cannot change between arrivals. */
    this._target = initial;

    // Start pessimistic (smooth) and tighten as evidence arrives -- the
    // reverse would stutter for the first second after every connect.
    /** Current delay, seconds.  Read every frame by the render clock. */
    this.value = initial;
  }

  /**
   * Call on every snapshot arrival.
   *
   * @param nowMs     local monotonic clock, ms -- the same clock update() gets.
   * @param serverMs  `snap.time` in MILLISECONDS.  Without the server's own
   *                  stamp there is no way to tell a slow link from a slow
   *                  client; the header says why.
   */
  noteArrival(nowMs, serverMs) {
    const prevArrival = this._lastArrival;
    const prevServer = this._lastServer;
    const hadPrev = this._hasPrev;
    this._lastArrival = nowMs;
    this._lastServer = serverMs;
    this._hasPrev = true;
    if (!hadPrev) return;

    const gap = nowMs - prevArrival;
    const step = serverMs - prevServer;
    // Both clocks must have moved forward by a believable amount.  A server
    // clock that went backwards is a restart, not a measurement.
    if (!(gap > 0 && gap < ABSURD_MS)) return;
    if (!(step > 0 && step < ABSURD_MS)) return;

    // How long this thread made the message wait.  update() runs at the TOP
    // of the frame, before any rendering, so this spans the frame's own work:
    // large exactly when we were busy, ~0 when we were idle.
    const hold = this._lastFrameAt > 0 ? Math.max(0, nowMs - this._lastFrameAt) : 0;

    // A block longer than a whole snapshot interval does not merely delay
    // snapshots, it SWALLOWS them: several queue and drain back-to-back, and
    // their handler times say nothing about when the link delivered them.
    // Nothing is recoverable from such a sample, so it is discarded -- with
    // an escape hatch: once a whole window has gone by with nothing but
    // blocked samples this is not a hitch, it is what this machine does
    // (frames slower than 50 ms), and we go back to measuring as best we can
    // with the correction below rather than freezing on stale evidence.
    if (hold >= step) {
      this._blockedRun++;
      if (this._blockedRun <= WINDOW) return;
    } else {
      this._blockedRun = 0;
    }

    const late = nowMs - serverMs;
    // A drained queue produces lateness marching down from the stall length;
    // a server restart or wall-clock step moves the whole baseline at once.
    // Neither is jitter.  Reject anything absurdly above the best latency in
    // the window; the window's own turnover re-baselines within ~1.2 s if the
    // shift was permanent.
    if (this._n > 0 && late - this._minLate() > ABSURD_MS) return;

    this._late[this._w] = late;
    this._step[this._w] = step;
    // The frame grid delays a snapshot by at most the time this thread was
    // demonstrably unavailable, so `hold` is a hard ceiling on the lateness
    // -- and therefore on the lateness SPREAD -- the grid can have made up.
    // The ceiling, not the average, deliberately: how much of the sawtooth
    // lands inside one window depends on how the frame period beats against
    // the 50 ms snapshot interval, and a window catching it mid-wrap reads
    // nearly the full `hold` as spread.  Calibrating against the average
    // leaves the original bug alive at those frame periods.
    this._self[this._w] = hold;
    this._w = (this._w + 1) % WINDOW;
    if (this._n < WINDOW) this._n++;
    this._recompute();
  }

  /**
   * Ease toward the measured target, and record that a frame started.
   *
   * @param dt     frame delta, seconds.
   * @param nowMs  the same clock noteArrival is given, sampled at the TOP of
   *               the frame -- a stamp taken after the frame's work would
   *               report a thread that is never busy.
   * @returns the delay to render at, seconds.
   */
  update(dt, nowMs) {
    this._lastFrameAt = nowMs;
    if (this._n < MIN_SAMPLES) return this.value;

    // Asymmetric on purpose: widen fast (a starved buffer stutters
    // immediately and is very visible), narrow slowly (chasing a brief calm
    // patch would oscillate).
    const rate = this._target > this.value ? 6 : 0.7;
    this.value += (this._target - this.value) * (1 - Math.exp(-rate * dt));
    return this.value;
  }

  // --- the estimate ---------------------------------------------------------

  _recompute() {
    if (this._n < MIN_SAMPLES) return;

    // The base interval comes from the SERVER's spacing, not ours: on a
    // client whose frames re-grid arrivals, the local median measures the
    // frame period, which is not a snapshot interval -- it is a shorter clock.
    this._sortInto(this._step);
    const base = this._at(0.5);

    // One-way latency variation: the thing a jitter buffer exists to absorb.
    this._sortInto(this._late);
    const spread = Math.max(0, this._at(0.9) - this._at(0.5));

    // The share of that spread this thread can be PROVEN to have caused,
    // taken as the MINIMUM over the window: the correction is only ever as
    // large as the block every single sample sat behind.  One responsive
    // moment collapses it to nothing -- the safe direction.
    let mine = Infinity;
    for (let k = 0; k < this._n; k++) if (this._self[k] < mine) mine = this._self[k];

    // Subtract in quadrature, not linearly: link jitter and the frame grid
    // are independent noise sources, so their spreads add as variances.
    // Linear subtraction would gut a real 40 ms jitter estimate the moment
    // the client also happened to be busy -- the one client that needs the
    // margin most.
    const jitter = Math.sqrt(Math.max(0, spread * spread - mine * mine));

    this._target = Math.min(
      INTERP_MAX,
      Math.max(INTERP_MIN, (base + jitter * INTERP_JITTER_MULT) / 1000),
    );
  }

  _minLate() {
    let m = Infinity;
    for (let k = 0; k < this._n; k++) if (this._late[k] < m) m = this._late[k];
    return m;
  }

  /** Insertion sort of src[0.._n) into the scratch ring.  n is 24 and this
      runs at snapshot rate, so it beats the allocation every other sort
      needs (subarray and [...] both allocate; Array#sort wants a comparator). */
  _sortInto(src) {
    const s = this._scratch;
    for (let k = 0; k < this._n; k++) {
      const v = src[k];
      let j = k - 1;
      while (j >= 0 && s[j] > v) {
        s[j + 1] = s[j];
        j--;
      }
      s[j + 1] = v;
    }
  }

  /** Percentile p in 0..1, against the last _sortInto. */
  _at(p) {
    return this._scratch[Math.min(this._n - 1, Math.floor(this._n * p))];
  }
}
